import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const translations = {
  title: { es: "Cuéntanos quién eres", en: "Tell us who you are" },
  subtitle: {
    es: "Déjanos tus datos y te contactaremos en cuanto este curso esté disponible.",
    en: "Leave your details and we'll reach out as soon as this course is available.",
  },
  subtitleGeneric: {
    es: "Déjanos tus datos y te avisaremos en cuanto los cursos estén disponibles.",
    en: "Leave your details and we'll notify you as soon as courses are available.",
  },
  courseLabel: { es: "Curso de interés", en: "Course of interest" },
  namePlaceholder: { es: "Nombre completo", en: "Full name" },
  emailPlaceholder: { es: "Correo electrónico", en: "Email address" },
  messagePlaceholder: {
    es: "Cuéntanos qué necesitas (opcional)",
    en: "Tell us what you need (optional)",
  },
  submit: { es: "Quiero más información", en: "Request more information" },
  sending: { es: "Enviando…", en: "Sending…" },
  success: {
    es: "¡Gracias! Te contactaremos pronto.",
    en: "Thanks! We'll be in touch soon.",
  },
  error: {
    es: "Hubo un problema. Intenta de nuevo.",
    en: "Something went wrong. Please try again.",
  },
  close: { es: "Cerrar", en: "Close" },
  privacy: {
    es: "Al enviar aceptas nuestra Política de Privacidad.",
    en: "By submitting you accept our Privacy Policy.",
  },
};

const InterestModal = ({ open, onClose, course }) => {
  const { t, language } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      const onEsc = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onEsc);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", onEsc);
      };
    }
  }, [open, onClose]);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setMessage("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/interest`, {
        name: name.trim(),
        email: email.trim(),
        course_id: course?.id ?? null,
        course_name: course?.name ?? null,
        message: message.trim() || null,
        language,
      });
      toast.success(t(translations.success));
      onClose();
    } catch (err) {
      toast.error(t(translations.error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          data-testid="interest-modal"
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0B1F3B] border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="interest-modal-close"
              aria-label={t(translations.close)}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 text-white/60 hover:text-white flex items-center justify-center transition-all hover:rotate-90 duration-300"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="mb-6">
              <h3 className="font-display text-2xl md:text-3xl text-white font-medium tracking-tight mb-2">
                {t(translations.title)}
              </h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">
                {t(course ? translations.subtitle : translations.subtitleGeneric)}
              </p>
            </div>

            {course && (
              <div className="mb-5 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl">
                <div className="font-body text-[10px] text-[#F59E0B] uppercase tracking-wider font-bold mb-0.5">
                  {t(translations.courseLabel)}
                </div>
                <div className="font-display text-white text-sm font-semibold">
                  {course.name}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="interest-form">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(translations.namePlaceholder)}
                required
                data-testid="interest-input-name"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 font-body focus:outline-none focus:border-[#2ED3C6] focus:bg-white/10 transition-all"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t(translations.emailPlaceholder)}
                required
                data-testid="interest-input-email"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 font-body focus:outline-none focus:border-[#2ED3C6] focus:bg-white/10 transition-all"
              />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t(translations.messagePlaceholder)}
                rows={3}
                data-testid="interest-input-message"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 font-body focus:outline-none focus:border-[#2ED3C6] focus:bg-white/10 transition-all resize-none"
              />
              <button
                type="submit"
                disabled={submitting || !name.trim() || !email.trim()}
                data-testid="interest-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-50 disabled:cursor-not-allowed text-[#0B1F3B] font-display font-bold py-3.5 px-6 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#F59E0B]/30 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t(translations.sending)}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t(translations.submit)}
                  </>
                )}
              </button>
              <p className="text-center font-body text-[11px] text-white/40 pt-1">
                {t(translations.privacy)}
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InterestModal;
